'use client'

import React from 'react'
import { Header } from '@/payload-types'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Link } from '@/i18n/routing'
import { cn } from '@/utilities/cn'
import { useTranslations } from 'next-intl'
import { usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'

interface HeaderNavProps {
  header: Header
}

// Prevent hover from opening dropdown — click only
const preventHover = {
  onPointerEnter: (e: React.PointerEvent) => e.preventDefault(),
  onPointerMove: (e: React.PointerEvent) => e.preventDefault(),
  onPointerLeave: (e: React.PointerEvent) => e.preventDefault(),
}

type NavLinkField = {
  type?: ('reference' | 'custom') | null
  url?: string | null
  reference?: {
    relationTo: string
    value: unknown
  } | null
}

// CODE-04 (Wave 2): category URLs now resolve via the shared utility so
// the header points at /{slug} instead of /shop?category=NN. See
// src/utilities/categoryHref.ts. The inline duplication is gone.
import { getCategoryHref } from '@/utilities/categoryHref'

function resolveUrl(
  link: NavLinkField,
  categoryId?: { id?: string | number } | number | null,
): string {
  if (categoryId !== undefined && categoryId !== null) {
    return getCategoryHref(categoryId, link.url)
  }
  if (
    link.type === 'reference' &&
    link.reference &&
    typeof link.reference.value === 'object' &&
    link.reference.value !== null &&
    'slug' in (link.reference.value as object)
  ) {
    const slug = (link.reference.value as { slug?: string | null }).slug
    if (slug) return `/${slug}`
  }
  return link.url || '#'
}

export function HeaderNav({ header }: HeaderNavProps) {
  const navItems = header.navItems || []
  const t = useTranslations('common')
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isActive = (url: string) => {
    if (url === '#') return false
    if (url.startsWith('/shop?category=')) {
      const catId = url.split('category=')[1]
      return pathname === '/shop' && searchParams.get('category') === catId
    }
    return pathname === url
  }

  // Premium nav-item styling: gold text-glow + an underline that draws in
  // from the center on hover/focus and stays lit while active or open.
  const navItemBase = cn(
    'group relative inline-flex h-full items-center font-mono uppercase tracking-wider text-sm',
    '!bg-transparent hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent',
    'transition-[color,text-shadow] duration-200',
    'after:pointer-events-none after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[2px] after:rounded-full',
    'after:bg-[var(--brand-gold)] after:shadow-[0_0_8px_rgb(var(--brand-gold-rgb)_/_0.65)]',
    'after:origin-center after:scale-x-0 after:transition-transform after:duration-300 after:ease-out',
    'motion-reduce:after:transition-none',
  )
  const navItemInactive = cn(
    'text-white hover:text-[var(--brand-gold)] focus:text-[var(--brand-gold)] data-[state=open]:text-[var(--brand-gold)]',
    'hover:[text-shadow:0_0_12px_rgb(var(--brand-gold-rgb)_/_0.45)]',
    'hover:after:scale-x-100 focus-visible:after:scale-x-100 data-[state=open]:after:scale-x-100',
  )
  const navItemActive = cn(
    'text-[var(--brand-gold)] [text-shadow:0_0_12px_rgb(var(--brand-gold-rgb)_/_0.4)] after:scale-x-100',
  )

  return (
    <NavigationMenu className="h-full">
      <NavigationMenuList className="h-full gap-1 lg:gap-2">
        {navItems.map((item) => {
          const hasSubNav = item.subNavItems && item.subNavItems.length > 0
          const itemUrl = resolveUrl(item.link, item.categoryId)

          if (hasSubNav) {
            const isParentActive =
              isActive(itemUrl) ||
              item.subNavItems?.some((sub) => {
                const subUrl = resolveUrl(sub.link, sub.categoryId)
                if (isActive(subUrl)) return true
                return sub.subSubNavItems?.some((subsub) => isActive(resolveUrl(subsub.link, subsub.categoryId)))
              })

            return (
              <NavigationMenuItem key={item.id} className="h-full flex items-center">
                <NavigationMenuTrigger
                  className={cn(navItemBase, isParentActive ? navItemActive : navItemInactive)}
                  {...preventHover}
                >
                  {item.link.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  onPointerEnter={(e) => e.preventDefault()}
                  onPointerLeave={(e) => e.preventDefault()}
                >
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[600px] md:grid-cols-3 lg:w-[800px] max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <li className="col-span-full border-b border-white/10 pb-3 mb-1">
                      <NavigationMenuLink asChild>
                        <Link
                          href={itemUrl !== '#' ? itemUrl : '/shop'}
                          className="text-sm font-mono font-semibold uppercase tracking-wider text-[var(--brand-gold)] hover:underline outline-none transition-colors"
                        >
                          {t('viewAll')} — {item.link.label}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    {(!item.subNavItems || item.subNavItems.length === 0) && (
                      <li className="col-span-full text-sm text-muted-foreground font-mono py-2">
                        Keine Unterkategorien vorhanden
                      </li>
                    )}
                    {item.subNavItems?.map((subItem) => {
                      const subItemUrl = resolveUrl(subItem.link, subItem.categoryId)
                      const hasSubSubNav = subItem.subSubNavItems && subItem.subSubNavItems.length > 0

                      return (
                        <li key={subItem.id} className="flex flex-col gap-2 p-2">
                          <NavigationMenuLink asChild>
                            <Link
                              href={subItemUrl}
                              className="text-base font-mono font-medium leading-none hover:text-[var(--brand-gold)] hover:underline outline-none uppercase tracking-tight transition-colors"
                            >
                              {subItem.link.label}
                            </Link>
                          </NavigationMenuLink>

                          {hasSubSubNav && (
                            <ul className="flex flex-col gap-2">
                              <li>
                                <NavigationMenuLink asChild>
                                  <Link
                                    href={subItemUrl !== '#' ? subItemUrl : '/shop'}
                                    className="text-xs font-mono font-semibold uppercase tracking-wider text-[var(--brand-gold)] hover:underline outline-none transition-colors"
                                  >
                                    {t('viewAll')}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                              {subItem.subSubNavItems?.map((subSubItem) => {
                                const subSubItemUrl = resolveUrl(subSubItem.link, subSubItem.categoryId)
                                return (
                                  <li key={subSubItem.id}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        href={subSubItemUrl}
                                        className="text-sm font-sans text-muted-foreground hover:text-[var(--brand-gold)] hover:underline line-clamp-1 outline-none transition-colors"
                                      >
                                        {subSubItem.link.label}
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            )
          }

          return (
            <NavigationMenuItem key={item.id} className="h-full flex items-center">
              <NavigationMenuLink asChild>
                <Link
                  href={itemUrl}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    navItemBase,
                    isActive(itemUrl) ? navItemActive : navItemInactive,
                  )}
                >
                  {item.link.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          )
        })}
      </NavigationMenuList>
    </NavigationMenu>
  )
}
